import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

// Status flow: draft → raised (requires PO PDF) → received (requires invoice PDF)
const STATUS_STYLE = {
  draft:    { bg: '#F3F4F6',   color: '#6B7280',      label: 'Draft'    },
  raised:   { bg: '#EFF6FF',   color: '#1D4ED8',      label: 'Raised'   },
  sent:     { bg: '#EFF6FF',   color: '#1D4ED8',      label: 'Raised'   }, // backward compat
  received: { bg: C.successBg, color: C.successText,  label: 'Received' },
};

const STATUS_STEPS = [
  { key: 'draft',    label: 'Draft',    icon: '📝', desc: 'PO is being prepared'          },
  { key: 'raised',   label: 'Raised',   icon: '📤', desc: 'Physical PO sent to vendor'    },
  { key: 'received', label: 'Received', icon: '✅', desc: 'Items received with invoice'    },
];

const BLANK_ITEM = { category_id: '', type_id: '', description: '', quantity: 1, unit_price: 0 };
const BLANK_PO   = { vendor_id: '', business_id: '', status: 'draft', notes: '', purchase_date: '', items: [{ ...BLANK_ITEM }] };

export default function PurchaseOrders() {
  const [orders,     setOrders]     = useState([]);
  const [vendors,    setVendors]    = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);

  // Filters
  const [search,    setSearch]    = useState('');
  const [stFilter,  setStFilter]  = useState('');
  const [vndFilter, setVndFilter] = useState('');

  // Insights + period filter (default: this month)
  const [insights,    setInsights]    = useState(null);
  const [insPeriod,   setInsPeriod]   = useState('this_month');  // this_month|last_month|all|custom
  const [insFrom,     setInsFrom]      = useState('');
  const [insTo,       setInsTo]        = useState('');

  // Inline row expansion
  const [expandedId,    setExpandedId]    = useState(null);
  const [detailCache,   setDetailCache]   = useState({});
  const [detailLoading, setDetailLoading] = useState(null);

  // Modals
  const [modal,       setModal]       = useState(null);  // 'form'|'view'|'delete'|'upload'
  const [selPO,       setSelPO]       = useState(null);
  const [form,        setForm]        = useState(BLANK_PO);
  const [saving,      setSaving]      = useState(false);

  // Upload (raise / receive)
  const [uploadMode,  setUploadMode]  = useState(null);  // 'raise'|'receive'
  const [uploadFile,  setUploadFile]  = useState(null);
  const [uploading,   setUploading]   = useState(false);

  // Per-item type cache { categoryId: [types] }
  const [typeCache, setTypeCache] = useState({});

  const [toast, setToast] = useState(null);
  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, vRes, bRes, cRes] = await Promise.all([
        api.get('/api/purchase-orders'),
        api.get('/api/vendors'),
        api.get('/api/businesses'),
        api.get('/api/categories'),
      ]);
      setOrders(oRes.data);
      setVendors(vRes.data);
      setBusinesses(bRes.data);
      setCategories(cRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadInsights = useCallback(async () => {
    try {
      const p = new URLSearchParams({ period: insPeriod });
      if (insPeriod === 'custom') { if (insFrom) p.set('date_from', insFrom); if (insTo) p.set('date_to', insTo); }
      const r = await api.get(`/api/purchase-orders/insights?${p.toString()}`);
      setInsights(r.data);
    } catch {}
  }, [insPeriod, insFrom, insTo]);
  useEffect(() => {
    // for custom, only fetch once at least one bound is set
    if (insPeriod === 'custom' && !insFrom && !insTo) return;
    loadInsights();
  }, [loadInsights, insPeriod, insFrom, insTo]);

  // ── Type cache ────────────────────────────────────────────────────────────

  const fetchTypes = useCallback(async (catId) => {
    if (!catId || typeCache[catId]) return;
    try {
      const r = await api.get(`/api/categories/${catId}/types`);
      setTypeCache(p => ({ ...p, [catId]: r.data }));
    } catch { setTypeCache(p => ({ ...p, [catId]: [] })); }
  }, [typeCache]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const fSet = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const itemSet = (idx, k, v) => setForm(p => {
    const items = p.items.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [k]: v };
      if (k === 'category_id') { next.type_id = ''; fetchTypes(v); }
      return next;
    });
    return { ...p, items };
  });

  const addItem    = () => setForm(p => ({ ...p, items: [...p.items, { ...BLANK_ITEM }] }));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const grandTotal = form.items.reduce((s, it) =>
    s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const openAdd = () => {
    setForm({ ...BLANK_PO, business_id: businesses[0]?.id || '', items: [{ ...BLANK_ITEM }] });
    setSelPO(null); setModal('form');
  };

  const openEdit = async (po) => {
    const r = await api.get(`/api/purchase-orders/${po.id}`);
    const full = r.data;
    // pre-fetch types for each category in the items
    full.items.forEach(it => { if (it.category_id) fetchTypes(it.category_id); });
    setForm({
      vendor_id:     full.vendor_id    || '',
      business_id:   full.business_id  || '',
      status:        full.status,
      notes:         full.notes || '',
      purchase_date: full.purchase_date || '',
      items: full.items.length
        ? full.items.map(it => ({
            category_id: it.category_id || '',
            type_id:     it.type_id     || '',
            description: it.description || '',
            quantity:    it.quantity,
            unit_price:  it.unit_price,
          }))
        : [{ ...BLANK_ITEM }],
    });
    setSelPO(full); setModal('form');
  };

  // Inline expand-below-row: fetch full detail (with insights + devices) once, cache it
  const toggleExpand = async (po) => {
    if (expandedId === po.id) { setExpandedId(null); return; }
    setExpandedId(po.id);
    if (!detailCache[po.id]) {
      setDetailLoading(po.id);
      try {
        const r = await api.get(`/api/purchase-orders/${po.id}`);
        setDetailCache(c => ({ ...c, [po.id]: r.data }));
      } catch {} finally { setDetailLoading(null); }
    }
  };
  const openDelete = (po) => { setSelPO(po); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelPO(null); };

  const save = async () => {
    if (!form.items.length) return showToast('error', 'Add at least one item.');
    setSaving(true);
    try {
      const body = {
        vendor_id:     form.vendor_id     ? Number(form.vendor_id)   : null,
        business_id:   form.business_id   ? Number(form.business_id) : null,
        status:        form.status,
        notes:         form.notes || null,
        purchase_date: form.purchase_date || null,
        items: form.items.map(it => ({
          category_id: it.category_id ? Number(it.category_id) : null,
          type_id:     it.type_id     ? Number(it.type_id)     : null,
          description: it.description || null,
          quantity:    Number(it.quantity)   || 1,
          unit_price:  Number(it.unit_price) || 0,
        })),
      };
      if (selPO) await api.put(`/api/purchase-orders/${selPO.id}`, body);
      else        await api.post('/api/purchase-orders', body);
      await loadAll(); loadInsights();
      showToast('success', selPO ? 'Purchase order updated.' : 'Purchase order created.');
      closeModal();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  // Open upload modal for Raise or Receive
  const openUpload = (po, mode) => {
    setSelPO(po); setUploadMode(mode); setUploadFile(null); setModal('upload');
  };

  // Submit file upload → transitions status
  const doUpload = async () => {
    if (!uploadFile) return showToast('error', 'Please select a PDF file.');
    if (uploadFile.type !== 'application/pdf') return showToast('error', 'Only PDF files are accepted.');
    const fd = new FormData();
    fd.append('file', uploadFile);
    setUploading(true);
    try {
      const ep = uploadMode === 'raise'
        ? `/api/purchase-orders/${selPO.id}/upload-po-doc`
        : `/api/purchase-orders/${selPO.id}/upload-invoice`;
      await api.post(ep, fd);
      await loadAll(); loadInsights();
      showToast('success', uploadMode === 'raise'
        ? `${selPO.po_number} raised — PO document saved.`
        : `${selPO.po_number} marked as Received — invoice saved.`);
      closeModal();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Upload failed.');
    } finally { setUploading(false); }
  };

  // View a stored PDF in a new tab
  const viewFile = async (poId, fileType) => {
    try {
      const r = await api.get(`/api/purchase-orders/${poId}/files/${fileType}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { showToast('error', 'File not found.'); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/purchase-orders/${selPO.id}`);
      await loadAll(); loadInsights();
      showToast('success', 'Purchase order deleted.');
      closeModal();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  // ── Filters ───────────────────────────────────────────────────────────────

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    return (
      (!q || o.po_number?.toLowerCase().includes(q) || o.vendor_name?.toLowerCase().includes(q))
      && (!stFilter  || o.status    === stFilter)
      && (!vndFilter || String(o.vendor_id) === String(vndFilter))
    );
  });

  const totalValue = orders.reduce((s, o) => s + (o.total_amount || 0), 0);

  return (
    <Layout title="Purchase Orders">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Total Orders', val: orders.length,                                                                          dot: C.primary  },
          { label: 'Draft',        val: orders.filter(o => o.status === 'draft').length,                                        dot: '#9CA3AF'  },
          { label: 'Raised',       val: orders.filter(o => o.status === 'raised' || o.status === 'sent').length,                dot: '#1D4ED8'  },
          { label: 'Received',     val: orders.filter(o => o.status === 'received').length,                                     dot: C.success  },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
            padding: '13px 18px', display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <div style={{
          background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
          borderRadius: '10px', padding: '13px 20px', color: '#fff',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 600, opacity: 0.8, marginBottom: '2px' }}>TOTAL VALUE</div>
          <div style={{ fontSize: '18px', fontWeight: 800 }}>₹ {totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
        </div>
        <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + New Purchase Order
        </button>
      </div>

      {/* ── Spend vs Recovered insight ── */}
      {insights && (() => {
        const t = insights.totals;
        const inProfit = t.net >= 0;
        const recPct = Math.min(100, t.recovery_pct);
        const hasData = t.units_purchased > 0;
        const PERIODS = [
          { key: 'this_month', label: 'This Month' },
          { key: 'last_month', label: 'Last Month' },
          { key: 'all',        label: 'All Time' },
          { key: 'custom',     label: 'Custom' },
        ];
        return (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '18px 22px', marginBottom: '22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: C.textMain, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                💹 Spend vs Recovered
                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 700, color: C.primaryDeep, textTransform: 'none', letterSpacing: 0 }}>· {insights.period_label}</span>
              </div>

              {/* Period filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {PERIODS.map(p => (
                  <button key={p.key} onClick={() => setInsPeriod(p.key)} style={{
                    padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                    border: `1.5px solid ${insPeriod === p.key ? C.primary : C.border}`,
                    background: insPeriod === p.key ? C.primary : C.white,
                    color: insPeriod === p.key ? '#fff' : C.textSub,
                  }}>{p.label}</button>
                ))}
                {insPeriod === 'custom' && (
                  <>
                    <input type="date" value={insFrom} onChange={e => setInsFrom(e.target.value)}
                      style={{ padding: '4px 8px', border: `1.5px solid ${C.border}`, borderRadius: '7px', fontSize: '11px', outline: 'none' }} />
                    <span style={{ color: C.textSub, fontSize: '11px' }}>→</span>
                    <input type="date" value={insTo} onChange={e => setInsTo(e.target.value)}
                      style={{ padding: '4px 8px', border: `1.5px solid ${C.border}`, borderRadius: '7px', fontSize: '11px', outline: 'none' }} />
                  </>
                )}
              </div>
            </div>

            {!hasData ? (
              <div style={{ padding: '18px', textAlign: 'center', fontSize: '12px', color: C.textSub }}>
                No purchase orders in <strong>{insights.period_label}</strong>.
                {insPeriod === 'custom' && !insFrom && !insTo && ' Pick a date range.'}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '11px', color: C.textSub, marginBottom: '10px' }}>
                  {t.po_count} order{t.po_count !== 1 ? 's' : ''} · {t.units_purchased} units
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  <InsightTile label="Spent (procurement)" value={t.cost_spent} color="#B45309" bg="#FEF3C7" />
                  <InsightTile label="Recovered" value={t.total_recovered} color="#065F46" bg="#D1FAE5"
                    sub={`product ₹${t.product_revenue.toLocaleString('en-IN')} + AMC ₹${t.amc_earned.toLocaleString('en-IN')}`} />
                  <InsightTile label={inProfit ? 'Net Gain' : 'Net Shortfall'} value={t.net} color={inProfit ? '#065F46' : '#991B1B'} bg={inProfit ? '#ECFDF5' : '#FEE2E2'} signed />
                  <InsightTile label="Locked in unsold stock" value={t.locked_in_stock} color="#5B21B6" bg="#F5F3FF" />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1, height: '10px', background: '#F3F4F6', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ width: `${recPct}%`, height: '100%', background: inProfit ? 'linear-gradient(90deg,#10B981,#059669)' : 'linear-gradient(90deg,#F59E0B,#D97706)', borderRadius: '6px', transition: 'width 0.5s' }} />
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: inProfit ? '#065F46' : '#B45309', whiteSpace: 'nowrap' }}>
                    {t.recovery_pct}% recovered
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginTop: '12px', fontSize: '11px', color: C.textSub }}>
                  <span>📦 Deployed: <strong style={{ color: C.textMain }}>{t.units_deployed}</strong></span>
                  <span>🏬 In stock: <strong style={{ color: C.textMain }}>{t.units_in_stock}</strong></span>
                  <span>⚠️ Write-off: <strong style={{ color: C.textMain }}>{t.units_writeoff}</strong></span>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Table */}
      <div style={card}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search PO number or vendor…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
          <select value={vndFilter} onChange={e => setVndFilter(e.target.value)} style={selS}>
            <option value="">All Vendors</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
          </select>
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selS}>
            <option value="">All Status</option>
            {Object.entries(STATUS_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {(search || stFilter || vndFilter) && (
            <button onClick={() => { setSearch(''); setStFilter(''); setVndFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {orders.length}</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '780px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['PO Number', 'Vendor', 'Business', 'Total Amount', 'Status', 'Progress', 'Date', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={emptyCell}>
                  {orders.length === 0 ? 'No purchase orders yet. Click "+ New Purchase Order" to start.' : 'No results.'}
                </td></tr>
              ) : filtered.map((o, i) => {
                const ss = STATUS_STYLE[o.status] || STATUS_STYLE.draft;
                const isExp = expandedId === o.id;
                return (
                  <React.Fragment key={o.id}>
                  <tr
                    onClick={() => toggleExpand(o)}
                    title="Click for details"
                    style={{ background: isExp ? C.primaryBg : i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: isExp ? 'none' : `1px solid ${C.border}`, cursor: 'pointer' }}
                    onMouseEnter={e => { if (!isExp) e.currentTarget.style.background = C.primaryBg; }}
                    onMouseLeave={e => { if (!isExp) e.currentTarget.style.background = i % 2 === 0 ? C.white : '#FFFBF5'; }}
                  >
                    <td style={td}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 700, color: C.primaryDeep, fontFamily: 'monospace' }}>
                        <span style={{ fontSize: '10px', color: C.textSub, transform: isExp ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
                        {o.po_number}
                      </span>
                    </td>
                    <td style={td}>{o.vendor_name   || <span style={{ color: C.textSub }}>—</span>}</td>
                    <td style={td}><span style={{ fontSize: '12px', color: C.textSub }}>{o.business_name || '—'}</span></td>
                    <td style={td}>
                      <span style={{ fontWeight: 700, color: C.textMain }}>
                        ₹ {(o.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{
                        background: ss.bg, color: ss.color,
                        padding: '3px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                      }}>{ss.label}</span>
                    </td>
                    {/* Progress action */}
                    <td style={td} onClick={e => e.stopPropagation()}>
                      {o.status === 'draft' && (
                        <button onClick={() => openUpload(o, 'raise')} style={{
                          padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                          background: '#EFF6FF', color: '#1D4ED8',
                          border: '1px solid #BFDBFE', cursor: 'pointer',
                        }}>📤 Raise PO</button>
                      )}
                      {(o.status === 'raised' || o.status === 'sent') && (
                        <button onClick={() => openUpload(o, 'receive')} style={{
                          padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                          background: C.successBg, color: C.successText,
                          border: `1px solid ${C.success}30`, cursor: 'pointer',
                        }}>✅ Mark Received</button>
                      )}
                      {o.status === 'received' && (
                        <span style={{ fontSize: '11px', color: C.success, fontWeight: 600 }}>✓ Completed</span>
                      )}
                    </td>
                    <td style={td}>
                      {o.purchase_date
                        ? <span style={{ fontSize: '12px', color: C.textMain, fontWeight: 600 }}>{o.purchase_date}</span>
                        : <span style={{ fontSize: '12px', color: C.textSub }}>—</span>}
                      <div style={{ fontSize: '10px', color: C.textSub, marginTop: '2px' }}>Added: {o.created_at}</div>
                    </td>
                    <td style={td} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {o.status !== 'received' && <SmBtn label="Edit" color="#3B82F6" onClick={() => openEdit(o)} />}
                        <SmBtn label="Delete" color={C.danger} onClick={() => openDelete(o)} />
                      </div>
                    </td>
                  </tr>

                  {/* Inline detail row */}
                  {isExp && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, borderBottom: `2px solid ${C.primary}22`, background: '#FCFCFD' }}>
                        <PODetail
                          po={detailCache[o.id]}
                          loading={detailLoading === o.id}
                          onEdit={() => openEdit(o)}
                          onRaise={() => openUpload(o, 'raise')}
                          onReceive={() => openUpload(o, 'receive')}
                          onViewFile={(kind) => viewFile(o.id, kind)}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal === 'form' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '860px' }}>
            <MHeader title={selPO ? `Edit — ${selPO.po_number}` : 'New Purchase Order'} onClose={closeModal} />

            <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: '76vh' }}>

              {/* Header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <FF label="Vendor">
                  <FSel value={form.vendor_id} onChange={v => fSet('vendor_id', v)}>
                    <option value="">— No Vendor —</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                  </FSel>
                </FF>
                <FF label="Business">
                  <FSel value={form.business_id} onChange={v => fSet('business_id', v)}>
                    <option value="">— No Business —</option>
                    {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </FSel>
                </FF>
                <FF label="Purchase Date">
                  <input
                    type="date"
                    value={form.purchase_date}
                    onChange={e => fSet('purchase_date', e.target.value)}
                    style={{ ...inputBase }}
                  />
                </FF>
                <FF label="Status">
                  <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                    {STATUS_STEPS.map((step, i) => {
                      const effectiveStatus = form.status === 'sent' ? 'raised' : form.status;
                      const stepIdx = STATUS_STEPS.findIndex(s => s.key === step.key);
                      const curIdx  = STATUS_STEPS.findIndex(s => s.key === effectiveStatus);
                      const done    = stepIdx < curIdx;
                      const active  = step.key === effectiveStatus;
                      const ss      = STATUS_STYLE[step.key];
                      return (
                        <div key={step.key} style={{ flex: 1, padding: '7px 8px', borderRadius: '8px', textAlign: 'center',
                          border: `2px solid ${active ? ss.color : done ? ss.color + '80' : C.border}`,
                          background: active ? ss.bg : done ? ss.bg + '60' : '#FAFAFA',
                          opacity: done ? 0.7 : 1,
                        }}>
                          <div style={{ fontSize: '14px', marginBottom: '1px' }}>{step.icon}</div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: active ? ss.color : done ? ss.color : C.textSub }}>{step.label}</div>
                          {done && <div style={{ fontSize: '9px', color: ss.color }}>✓</div>}
                        </div>
                      );
                    })}
                  </div>
                  {(form.status === 'draft' || !form.status) && (
                    <div style={{ fontSize: '10px', color: C.textSub, marginTop: '5px' }}>
                      Status advances via PDF upload — use Raise PO / Mark Received actions.
                    </div>
                  )}
                </FF>
              </div>

              <FF label="Notes">
                <textarea value={form.notes} onChange={e => fSet('notes', e.target.value)}
                  placeholder="Internal notes…" rows={2}
                  style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
              </FF>

              {/* Items */}
              <div style={{ marginTop: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Order Items
                  </div>
                  <button onClick={addItem} style={{ ...btnPrimary, padding: '6px 14px', fontSize: '12px' }}>+ Add Item</button>
                </div>

                <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: C.primaryBg }}>
                        {['#', 'Product Category', 'Type', 'Description', 'Qty', 'Unit Price', 'Total', ''].map(h => (
                          <th key={h} style={{ ...th, padding: '9px 10px', fontSize: '10px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, idx) => {
                        const lineTotal  = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                        const catTypes   = (it.category_id && typeCache[it.category_id]) || [];
                        return (
                          <tr key={idx} style={{ borderTop: `1px solid ${C.border}` }}>
                            <td style={{ padding: '6px 10px', color: C.textSub, fontSize: '12px', width: '28px' }}>{idx + 1}</td>

                            {/* Category */}
                            <td style={{ padding: '5px 6px', minWidth: '150px' }}>
                              <select value={it.category_id}
                                onChange={e => itemSet(idx, 'category_id', e.target.value)}
                                style={{ ...inputBase, padding: '7px 8px', fontSize: '12px' }}>
                                <option value="">— Category —</option>
                                {categories.map(c => (
                                  <option key={c.id} value={c.id}>
                                    {c.product_name ? `${c.product_name} › ` : ''}{c.name}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* Type — only shown when category has types */}
                            <td style={{ padding: '5px 6px', minWidth: '130px' }}>
                              <select value={it.type_id}
                                onChange={e => itemSet(idx, 'type_id', e.target.value)}
                                disabled={!catTypes.length}
                                style={{
                                  ...inputBase, padding: '7px 8px', fontSize: '12px',
                                  background: catTypes.length ? C.white : '#F9FAFB',
                                  color: catTypes.length ? C.textMain : C.textSub,
                                }}>
                                <option value="">{catTypes.length ? '— Select Type —' : '—'}</option>
                                {catTypes.map(t => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}{t.amc_amount ? ` (₹${t.amc_amount})` : ''}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* Description */}
                            <td style={{ padding: '5px 6px', minWidth: '120px' }}>
                              <input value={it.description}
                                onChange={e => itemSet(idx, 'description', e.target.value)}
                                placeholder="Description…"
                                style={{ ...inputBase, padding: '7px 8px', fontSize: '12px' }} />
                            </td>

                            {/* Qty */}
                            <td style={{ padding: '5px 6px', width: '65px' }}>
                              <input type="number" min="1" value={it.quantity}
                                onChange={e => itemSet(idx, 'quantity', e.target.value)}
                                style={{ ...inputBase, padding: '7px 6px', fontSize: '12px', textAlign: 'center' }} />
                            </td>

                            {/* Unit price */}
                            <td style={{ padding: '5px 6px', width: '100px' }}>
                              <input type="number" min="0" step="0.01" value={it.unit_price}
                                onChange={e => itemSet(idx, 'unit_price', e.target.value)}
                                style={{ ...inputBase, padding: '7px 6px', fontSize: '12px', textAlign: 'right' }} />
                            </td>

                            {/* Total */}
                            <td style={{ padding: '6px 10px', width: '90px', textAlign: 'right' }}>
                              <span style={{ fontWeight: 700, color: C.textMain, fontSize: '13px' }}>
                                ₹ {lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>

                            {/* Remove */}
                            <td style={{ padding: '5px 6px', width: '32px' }}>
                              {form.items.length > 1 && (
                                <button onClick={() => removeItem(idx)} style={{
                                  background: C.dangerBg, color: C.danger, border: 'none',
                                  borderRadius: '6px', width: '26px', height: '26px',
                                  cursor: 'pointer', fontWeight: 700, fontSize: '15px', lineHeight: 1,
                                }}>×</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Grand total */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                  <div style={{
                    background: C.sidebar, borderRadius: '10px', padding: '12px 22px',
                    display: 'flex', alignItems: 'center', gap: '18px',
                  }}>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.5px' }}>GRAND TOTAL</span>
                    <span style={{ fontSize: '22px', fontWeight: 800, color: C.primary }}>
                      ₹ {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <MFooter onClose={closeModal} onSave={save} saving={saving}
              label={selPO ? '💾 Update Order' : '+ Create Order'} />
          </div>
        </Overlay>
      )}

      {/* ── Upload Modal (Raise / Receive) ── */}
      {modal === 'upload' && selPO && (() => {
        const isRaise   = uploadMode === 'raise';
        const accentBg  = isRaise ? '#EFF6FF' : C.successBg;
        const accentCol = isRaise ? '#1D4ED8'  : C.successText;
        const accentBdr = isRaise ? '#BFDBFE'  : '#6EE7B7';
        return (
          <Overlay>
            <div style={{ ...modalBox, maxWidth: '460px' }}>
              <MHeader
                title={isRaise ? '📤 Raise Purchase Order' : '✅ Confirm Receipt'}
                onClose={closeModal}
              />
              <div style={{ padding: '24px 28px' }}>

                {/* Info banner */}
                <div style={{ background: accentBg, border: `1px solid ${accentBdr}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '22px' }}>{isRaise ? '📤' : '✅'}</span>
                  <div>
                    <div style={{ fontWeight: 700, color: accentCol, fontSize: '13px', marginBottom: '3px' }}>
                      {isRaise ? 'Upload Physical PO Document' : 'Upload Invoice PDF'}
                    </div>
                    <div style={{ fontSize: '12px', color: accentCol, opacity: 0.8, lineHeight: 1.5 }}>
                      {isRaise
                        ? `Attach the signed/sent physical PO document for ${selPO.po_number}. Status will change to Raised.`
                        : `Attach the vendor invoice for ${selPO.po_number}. Status will change to Received.`}
                    </div>
                  </div>
                </div>

                {/* PO summary */}
                <div style={{ fontSize: '12px', color: C.textSub, marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span>PO: <strong style={{ color: C.textMain, fontFamily: 'monospace' }}>{selPO.po_number}</strong></span>
                  <span>Vendor: <strong style={{ color: C.textMain }}>{selPO.vendor_name || '—'}</strong></span>
                  <span>Amount: <strong style={{ color: C.textMain }}>₹ {(selPO.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></span>
                </div>

                {/* File upload area */}
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <div style={{
                    border: `2px dashed ${uploadFile ? accentCol : C.border}`,
                    borderRadius: '12px',
                    padding: '24px 20px',
                    textAlign: 'center',
                    background: uploadFile ? accentBg : '#FAFAFA',
                    transition: 'all 0.2s',
                  }}>
                    {uploadFile ? (
                      <>
                        <div style={{ fontSize: '28px', marginBottom: '6px' }}>📄</div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: accentCol }}>{uploadFile.name}</div>
                        <div style={{ fontSize: '11px', color: C.textSub, marginTop: '3px' }}>
                          {(uploadFile.size / 1024).toFixed(1)} KB · PDF
                        </div>
                        <div style={{ fontSize: '11px', color: accentCol, marginTop: '6px', fontWeight: 600 }}>Click to change file</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>Click to select PDF</div>
                        <div style={{ fontSize: '11px', color: C.textSub, marginTop: '4px' }}>Only PDF files accepted · Max 20 MB</div>
                      </>
                    )}
                  </div>
                  <input type="file" accept=".pdf,application/pdf"
                    style={{ display: 'none' }}
                    onChange={e => setUploadFile(e.target.files?.[0] || null)} />
                </label>
              </div>

              {/* Footer */}
              <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={closeModal} style={btnGhost}>Cancel</button>
                <button onClick={doUpload} disabled={uploading || !uploadFile} style={{
                  ...btnPrimary,
                  background: uploading || !uploadFile
                    ? '#9CA3AF'
                    : isRaise
                      ? 'linear-gradient(135deg,#2563EB,#1D4ED8)'
                      : `linear-gradient(135deg,${C.success},#059669)`,
                  boxShadow: 'none',
                  cursor: uploading || !uploadFile ? 'not-allowed' : 'pointer',
                }}>
                  {uploading ? 'Uploading…' : isRaise ? '📤 Raise PO' : '✅ Confirm Received'}
                </button>
              </div>
            </div>
          </Overlay>
        );
      })()}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <MHeader title="Delete Purchase Order" onClose={closeModal} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: 0 }}>
                Delete <strong style={{ color: C.danger, fontFamily: 'monospace' }}>{selPO?.po_number}</strong>?
                <br /><span style={{ fontSize: '12px', color: C.textSub }}>All items in this order will also be deleted.</span>
              </p>
            </div>
            <MFooter onClose={closeModal} onSave={confirmDelete} saving={saving} label="Delete" danger />
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Shared components ─────────────────────────────────────────────────────────

// Compact inline PO detail — small font, short & sweet
function PODetail({ po, loading, onEdit, onRaise, onReceive, onViewFile }) {
  if (loading || !po) {
    return <div style={{ padding: '18px 24px', fontSize: '12px', color: C.textSub }}>Loading details…</div>;
  }
  const ins = po.insights || {};
  const inProfit = (ins.net || 0) >= 0;
  const rup = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
  const chip = { fontSize: '11px', padding: '2px 9px', borderRadius: '20px', fontWeight: 700, whiteSpace: 'nowrap' };

  return (
    <div style={{ padding: '14px 22px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* Meta + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '11px', color: C.textSub }}>
        <span>🏭 <strong style={{ color: C.textMain }}>{po.vendor_name || '—'}</strong></span>
        <span>🏢 {po.business_name || '—'}</span>
        <span>📅 {po.purchase_date || po.created_at}</span>
        {po.notes && <span style={{ fontStyle: 'italic' }}>“{po.notes}”</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
          {po.has_po_doc && <button onClick={onViewFile.bind(null, 'po-doc')} style={{ ...chip, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', cursor: 'pointer' }}>📄 PO</button>}
          {po.has_invoice && <button onClick={onViewFile.bind(null, 'invoice')} style={{ ...chip, background: C.successBg, color: C.successText, border: '1px solid #6EE7B7', cursor: 'pointer' }}>🧾 Invoice</button>}
          {po.status === 'draft' && <button onClick={onRaise} style={{ ...chip, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', cursor: 'pointer' }}>📤 Raise</button>}
          {(po.status === 'raised' || po.status === 'sent') && <button onClick={onReceive} style={{ ...chip, background: C.successBg, color: C.successText, border: '1px solid #6EE7B7', cursor: 'pointer' }}>✅ Receive</button>}
          {po.status !== 'received' && <button onClick={onEdit} style={{ ...chip, background: '#F3F4F6', color: C.textMain, border: `1px solid ${C.border}`, cursor: 'pointer' }}>✏ Edit</button>}
        </div>
      </div>

      {/* Insight strip */}
      {ins.units_purchased > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ ...chip, background: '#FEF3C7', color: '#B45309' }}>Spent {rup(ins.cost_spent)}</span>
          <span style={{ color: C.textSub }}>→</span>
          <span style={{ ...chip, background: '#D1FAE5', color: '#065F46' }}>Recovered {rup(ins.total_recovered)}</span>
          <span style={{ fontSize: '10px', color: C.textSub }}>(prod {rup(ins.product_revenue)} + AMC {rup(ins.amc_earned)})</span>
          <span style={{ ...chip, background: inProfit ? '#ECFDF5' : '#FEE2E2', color: inProfit ? '#065F46' : '#991B1B' }}>
            {inProfit ? '▲' : '▼'} Net {rup(Math.abs(ins.net))}
          </span>
          {/* recovery bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px', flex: 1 }}>
            <div style={{ flex: 1, height: '6px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, ins.recovery_pct)}%`, height: '100%', background: inProfit ? '#10B981' : '#F59E0B' }} />
            </div>
            <span style={{ fontSize: '11px', fontWeight: 800, color: inProfit ? '#065F46' : '#B45309' }}>{ins.recovery_pct}%</span>
          </div>
          <span style={{ fontSize: '10px', color: C.textSub, whiteSpace: 'nowrap' }}>
            {ins.units_purchased}u · 📦{ins.units_sold} 🏪{ins.units_dealer} 🏬{ins.units_in_stock}{ins.units_writeoff ? ` ⚠️${ins.units_writeoff}` : ''} · locked {rup(ins.locked_in_stock)}
          </span>
        </div>
      ) : (
        <div style={{ fontSize: '11px', color: C.textSub }}>
          Grand total {rup(po.total_amount)} · {po.items?.length || 0} line(s) — no stock received yet, so no recovery data.
        </div>
      )}

      {/* Devices */}
      {ins.devices?.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ background: C.primaryBg }}>
                {['Device', 'Serial / Ref', 'Category', 'Status', 'Now With', 'Value', 'AMC'].map(h => (
                  <th key={h} style={{ padding: '5px 10px', textAlign: 'left', fontSize: '9px', fontWeight: 800, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ins.devices.map((d, i) => (
                <tr key={d.inv_db_id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 ? '#FAFAFA' : C.white }}>
                  <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontWeight: 700, color: C.textMain, whiteSpace: 'nowrap' }}>{d.item_id}</td>
                  <td style={{ padding: '5px 10px', fontFamily: 'monospace', color: C.textSub, whiteSpace: 'nowrap' }}>
                    {d.serial_number || '—'}{d.ref_number ? ` · ${d.ref_number}` : ''}
                  </td>
                  <td style={{ padding: '5px 10px', color: C.textMain, whiteSpace: 'nowrap' }}>{d.category_name}{d.type_name ? ` · ${d.type_name}` : ''}</td>
                  <td style={{ padding: '5px 10px' }}><span style={poStatusPill(d.status)}>{d.status_label}</span></td>
                  <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                    <span style={{ color: d.holder_type === 'client' ? '#5B21B6' : d.holder_type === 'dealer' ? '#0F766E' : C.textSub, fontWeight: 600 }}>
                      {d.holder_type === 'dealer' ? '🏪 ' : d.holder_type === 'client' ? '👤 ' : ''}{d.holder}
                    </span>
                    {d.installer ? <span style={{ color: C.textSub }}> · 👷{d.installer}</span> : ''}
                  </td>
                  <td style={{ padding: '5px 10px', fontWeight: 700, color: d.value > 0 ? '#065F46' : C.textSub, whiteSpace: 'nowrap' }}>{d.value > 0 ? rup(d.value) : '—'}</td>
                  <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: d.amc_earned > 0 ? '#7C3AED' : C.textSub, fontWeight: d.amc_earned > 0 ? 700 : 400 }}>
                    {d.amc_earned > 0 ? rup(d.amc_earned) + (d.renewed_times > 1 ? ` ·${d.renewed_times}×` : '') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PO_DEV_STATUS = {
  'installed':   { bg: '#D1FAE5', color: '#065F46' },
  'dispatched':  { bg: '#DBEAFE', color: '#1D4ED8' },
  'packed':      { bg: '#EDE9FE', color: '#6D28D9' },
  'replacement': { bg: '#E0F2FE', color: '#0369A1' },
  'cancelled':   { bg: '#FEE2E2', color: '#991B1B' },
  'in-stock':    { bg: '#DCFCE7', color: '#15803D' },
  'dealer-stock':{ bg: '#F0FDFA', color: '#0F766E' },
  'damaged':     { bg: '#FEF3C7', color: '#92400E' },
};
function poStatusPill(status) {
  const s = PO_DEV_STATUS[status] || { bg: '#F3F4F6', color: '#374151' };
  return { background: s.bg, color: s.color, padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' };
}

function InsightTile({ label, value, color, bg, sub, signed }) {
  const v = Number(value || 0);
  const txt = (signed && v >= 0 ? '+' : v < 0 ? '−' : '') + '₹' + Math.abs(v).toLocaleString('en-IN');
  return (
    <div style={{ background: bg, borderRadius: '11px', padding: '13px 16px' }}>
      <div style={{ fontSize: '10px', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 900, color: '#111', marginTop: '3px' }}>{txt}</div>
      {sub && <div style={{ fontSize: '10px', color: C.textSub, marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function FF({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>{label}</label>
      {children}
    </div>
  );
}

function FSel({ value, onChange, children }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)} style={{ ...inputBase, cursor: 'pointer' }}>
      {children}
    </select>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '16px',
    }}>{children}</div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{
      padding: '20px 28px', borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function MFooter({ onClose, onSave, saving, label, danger = false }) {
  return (
    <div style={{
      padding: '14px 28px', borderTop: `1px solid ${C.border}`,
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
    }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputBase  = { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', boxSizing: 'border-box', background: C.white };
const card       = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox   = { background: C.white, borderRadius: '16px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th         = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td         = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell  = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selS       = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = { padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: `0 3px 10px rgba(245,166,35,0.3)` };
const btnGhost   = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnDanger  = { padding: '10px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
